import { apiDelete, apiGet, apiPost, apiPut } from '../support/helpers/ApiConnection.Helper';
import UserEntity from '../entities/User.Entity';
import UserAdminTableDataModel from '../data-models/UserAdminTableDataModel';
import UserWithRulesDataModel from '../data-models/UserWithRules.DataModel';

// Simple in-memory cache
let profileCache: {
    data: UserEntity | null;
    timestamp: number | null;
    loading: boolean;
} = {
    data: null,
    timestamp: null,
    loading: false
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function getProfile(abortSignal: AbortSignal): Promise<UserEntity> {
    // Check cache first
    if (profileCache.data && profileCache.timestamp) {
        const isExpired = (Date.now() - profileCache.timestamp) > CACHE_DURATION;
        if (!isExpired) {
            return Promise.resolve(profileCache.data);
        }
    }
    
    // If already loading, return a promise that waits for the current request
    if (profileCache.loading) {
        return new Promise((resolve, reject) => {
            const checkCache = setInterval(() => {
                if (!profileCache.loading) {
                    clearInterval(checkCache);
                    if (profileCache.data) {
                        resolve(profileCache.data);
                    } else {
                        reject(new Error('Failed to load profile'));
                    }
                }
            }, 100);
        });
    }
    
    // Make new request
    profileCache.loading = true;
    
    return apiGet<UserEntity>('/users', abortSignal)
        .then(profile => {
            profileCache.data = profile;
            profileCache.timestamp = Date.now();
            profileCache.loading = false;
            return profile;
        })
        .catch(error => {
            profileCache.loading = false;
            throw error;
        });
}

// Function to clear cache (useful for logout)
export function clearProfileCache() {
    profileCache.data = null;
    profileCache.timestamp = null;
    profileCache.loading = false;
}

// Function to refresh cache
export function refreshProfile(abortSignal: AbortSignal): Promise<UserEntity> {
    profileCache.data = null;
    profileCache.timestamp = null;
    return getProfile(abortSignal);
}

export function getUsers(abortSignal: AbortSignal): Promise<Array<UserAdminTableDataModel>> {
    return apiGet<Array<UserAdminTableDataModel>>('/users/users-admin-table', abortSignal);
}

export function deleteUser(userId: number, abortSignal: AbortSignal): Promise<UserEntity> {
    return apiDelete<UserEntity>('/users', userId, abortSignal);
}

export function createUser(newUser: UserEntity, abortSignal: AbortSignal): Promise<UserEntity> {
    return apiPost<UserEntity>('/users', newUser, abortSignal);
}

export function editUser(newUser: UserEntity, abortSignal: AbortSignal): Promise<UserEntity> {
    return apiPut<UserEntity>('/users', newUser.userId, newUser, abortSignal);
}

export function getUserProfile(userId: string, abortSignal: AbortSignal): Promise<UserWithRulesDataModel> {
    return apiGet<UserWithRulesDataModel>('/users/profile/' + userId, abortSignal);
}

export function userAssignSecurityProfile(
    userId: number,
    newSecurityProfilesIds: number[],
    removedSecurityProfileIds: number[],
    abortSignal: AbortSignal,
): Promise<boolean> {
    return apiPost<boolean>(
        '/users/profile/update-security-profiles/' + userId,
        { newSecurityProfilesIds, removedSecurityProfileIds },
        abortSignal,
    );
}
